// İSG uzmanı portalı — {ad, kod, oluşturulma} listesi için tekrar-kullanılır CRUD
// tablosu. Şube (§9.5) ve saha (§9.6) aynı sözleşmeyi paylaşır — tek bileşen.
// UTable sözleşmesi: page/pageSize/search + total/filteredTotal (api-contract §3).

import {
  type AdminListParams,
  Alert,
  Button,
  Card,
  CardContent,
  EmptyState,
  type PagedResult,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  currentCulture,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { Pencil, SearchX } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { NameCodeFormModal, type NameCodeValues } from './NameCodeFormModal';

const PAGE_SIZE = 20;

/** Ad + kod taşıyan liste satırı (şube/saha ortak yüzeyi). */
export interface NameCodeRow {
  uuid: string;
  name: string;
  code: string | null;
  createdAt: string | null;
}

interface EntityCrudTableProps<T extends NameCodeRow> {
  /** Liste sorgusunun taban anahtarı (parametreler eklenir; invalidasyon önekidir). */
  queryKeyBase: QueryKey;
  queryFn: (params: AdminListParams) => Promise<PagedResult<T>>;
  createSubmit: (values: NameCodeValues) => Promise<unknown>;
  updateSubmit: (entity: T, values: NameCodeValues) => Promise<unknown>;
  labels: {
    /** Boş liste ("veri yok") ikonu — S34-2 `EmptyState` table variant (design review §2).
     * Çağıran kendi varlığına (şube/saha) uygun bir `lucide-react` ikonu seçer; bu bileşen
     * hangi varlığın listelendiğini BİLMEZ. */
    icon: LucideIcon;
    caption: string;
    colName: string;
    colCode: string;
    newLabel: string;
    createTitle: string;
    editTitle: string;
    fieldName: string;
    fieldCode: string;
    searchPlaceholder: string;
    createdMessage: string;
    updatedMessage: string;
    /**
     * Araç çubuğu özet metni (S34-1 A4 — `{{count}} <entity> listeleniyor` deseni). Önceden
     * biçimlendirilmiş bir dize DEĞİL bir fonksiyon: `filteredTotal` yalnız bu bileşenin
     * İÇİNDEKİ sorgudan gelir, çağıran taraf render anında sayıyı henüz bilemez.
     */
    count: (count: number) => string;
  };
  /** Satır başına ek aksiyon (ör. şube → sahalar linki). */
  renderRowAction?: (entity: T) => ReactNode;
}

export function EntityCrudTable<T extends NameCodeRow>({
  queryKeyBase,
  queryFn,
  createSubmit,
  updateSubmit,
  labels,
  renderRowAction,
}: EntityCrudTableProps<T>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editEntity, setEditEntity] = useState<T | null>(null);

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const params: AdminListParams = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    sortBy: 'name',
    sortDir: 'asc',
  };

  const query = useQuery({
    queryKey: [...queryKeyBase, params],
    queryFn: () => queryFn(params),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeyBase });
  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const culture = currentCulture();
  const colSpan = renderRowAction ? 4 : 3;
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={query.data ? labels.count(query.data.filteredTotal) : undefined}
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('admin.common.search')}
            searchPlaceholder={labels.searchPlaceholder}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            onNew={() => {
              setMessage(null);
              setCreateOpen(true);
            }}
            newLabel={labels.newLabel}
          />

          <Table caption={labels.caption} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{labels.colName}</TableHead>
                <TableHead>{labels.colCode}</TableHead>
                <TableHead>{t('admin.common.createdAt')}</TableHead>
                <TableHead className="text-right">{t('admin.common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={colSpan} />
              ) : query.isError ? (
                <TableMessageRow colSpan={colSpan}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={colSpan}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('admin.common.empty')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={labels.icon}
                      title={t('admin.common.noData')}
                      description={t('admin.common.noData.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                          {labels.newLabel}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((entity) => (
                  <TableRow key={entity.uuid}>
                    <TableCell className="font-medium">{entity.name}</TableCell>
                    <TableCell className="text-muted">
                      {entity.code ? (
                        <code className="rounded bg-surface-muted px-1.5 py-0.5 text-xs">
                          {entity.code}
                        </code>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-muted">
                      {formatDate(entity.createdAt, culture)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {renderRowAction ? renderRowAction(entity) : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setEditEntity(entity);
                          }}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">{t('admin.common.edit')}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('admin.pagination.nav'),
                previous: t('admin.pagination.previous'),
                next: t('admin.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <NameCodeFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={labels.createTitle}
        nameLabel={labels.fieldName}
        codeLabel={labels.fieldCode}
        isEdit={false}
        submit={createSubmit}
        onSuccess={() => {
          invalidate();
          setMessage(labels.createdMessage);
        }}
      />
      <NameCodeFormModal
        open={editEntity !== null}
        onOpenChange={(open) => !open && setEditEntity(null)}
        title={labels.editTitle}
        nameLabel={labels.fieldName}
        codeLabel={labels.fieldCode}
        isEdit
        initial={editEntity ? { name: editEntity.name, code: editEntity.code } : null}
        submit={(values) => {
          if (!editEntity) {
            return Promise.reject(new Error('no entity'));
          }
          return updateSubmit(editEntity, values);
        }}
        onSuccess={() => {
          invalidate();
          setMessage(labels.updatedMessage);
        }}
      />
    </div>
  );
}
